import type { BlockIfData, InlineExpressionData } from '@gitbook/doc-core';
import type { Block, Inline } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, usePopoverClose } from '@gitbook/spine-react';

import { useNavigationState } from '../../../navigation';
import { useSpaceSiteAdaptiveContext } from '../../AdaptiveContent';
import { ExpressionEditor, type ExpressionEditorProps } from '../../Expressions';
import { VariablesSidePanelButton } from '../../Variables';

type ExpressionEditorNode = Block | Inline;
type ExpressionEditorNodeData = BlockIfData | InlineExpressionData;

/**
 * Popover expression editor for the adaptive content content editor nodes (i.e BlockIf & InlineExpression).
 */
export function ExpressionPopoverEditor(props: {
    node: ExpressionEditorNode;
    editor: EditorContainer;
    title: {
        editable: string;
        readOnly: string;
    };
    documentationURL?: ExpressionEditorProps['documentationURL'];
}) {
    const { node, editor, title, documentationURL } = props;
    const { expression } = node.data.toFrozenObject() as ExpressionEditorNodeData;
    const close = usePopoverClose();

    return (
        <div className="flex w-[480px] flex-col overflow-hidden">
            <div className="flex items-center gap-2 border-base border-b px-2 py-1">
                <Icon.Expression className="size-4 text-muted" />
                <div className="flex-1 text-muted text-ui-action">
                    {editor.readOnly ? title.readOnly : title.editable}
                </div>
                {!editor.readOnly ? <ExpressionEditorHeaderToolbar /> : null}
            </div>
            <ExpressionEditor
                size="large"
                value={expression}
                onChange={(value) => {
                    editor.change((change) => {
                        change.setDataByKey(node.key, ['expression'], value);
                    });
                }}
                onSubmit={close}
                readOnly={editor.readOnly}
                autoFocus
                autoSelect={!editor.readOnly}
                documentationURL={documentationURL}
            />
        </div>
    );
}

/**
 * Toolbar of the editor with button to access the visitor schema editor and variable sidepanel.
 */
function ExpressionEditorHeaderToolbar() {
    const { screen } = useNavigationState();
    const siteAdaptiveContext = useSpaceSiteAdaptiveContext();
    return (
        <div className="ml-auto flex items-center gap-2">
            {screen.type === 'space' && <VariablesSidePanelButton size="small" screen={screen} />}
            {siteAdaptiveContext.site ? (
                <Button
                    kind={siteAdaptiveContext.isUpgradeRequired ? 'upgrade' : 'blank'}
                    size="small"
                    onPress={() =>
                        siteAdaptiveContext.site?.adaptiveContent?.enabled
                            ? siteAdaptiveContext.openEditSchemaDialog()
                            : siteAdaptiveContext.openFeatureIntroDialog('inline-expr')
                    }
                    leadingIcon={Icon.Schema}
                    iconOnly
                    label={'Edit visitor schema'}
                />
            ) : null}
        </div>
    );
}
